import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  type AnyRoute,
} from "@tanstack/react-router";

import { SetPasswordScreen } from "@/auth/SetPasswordScreen";
import { SignInScreen } from "@/auth/SignInScreen";
import { validateSignInSearch } from "@/auth/sign-in-search";
import { hasSession } from "@/auth/backend";
import { NotificationSettings } from "@/components/notifications/NotificationSettings";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { NotFound } from "@/components/shell/NotFound";
import { PageSkeleton } from "@/components/shell/LoadingState";
import { RouteError } from "@/components/shell/RouteError";
import { encodeFilter } from "@/components/filters/url";
import { AttachmentRedirectRoute } from "@/routes/AttachmentRedirect";
import { SectionPage } from "@/routes/SectionPage";
import { SHIPPED_MODULES } from "@/config/modules";
import type { SectionSearch } from "@/routes/search";
import { validateSectionSearch } from "@/routes/search";

/**
 * Routes are generated from the navigation map, not hand-written.
 *
 * File-based routing would mean thirty-eight files that each restate a row of
 * §11.2, and the first time a section is renamed they disagree. The map in
 * `config/modules.ts` is the single source of truth for what exists; this
 * turns it into a route tree.
 */
const rootRoute = createRootRoute({
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

/**
 * The shell, as a pathless layout route rather than the root.
 *
 * `/sign-in` has to render with no sidebar at all (#270 §1), so
 * `ShellLayout` can no longer be the thing every route sits under — it is
 * now one branch of the tree, and the guard below is what keeps every
 * section route behind it.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: ShellLayout,
  /**
   * Anyone without a session lands on `/sign-in` instead of the section they
   * asked for, and `redirect` carries that section back so signing in
   * returns them to it — the alternative is a guard that always sends
   * people to the dashboard, which is indistinguishable from the app
   * forgetting where they were going.
   *
   * The answer comes from the API and is cached for the page's life, so a
   * session deleted on the server sends this browser back to sign-in on its
   * next reload rather than into a shell whose every request fails. It is
   * not the access check: that is `requirePermission()` on each route
   * (FR-ACL-7).
   */
  beforeLoad: async ({ location }) => {
    if (!(await hasSession())) {
      // `throw: true` rather than `throw redirect(...)` — the latter throws
      // a `Response`, not an `Error`, which `only-throw-error` flags even
      // though it is exactly what TanStack Router's own docs do.
      redirect({
        to: "/sign-in",
        search: { redirect: location.href },
        throw: true,
      });
    }
  },
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInScreen,
  validateSearch: validateSignInSearch,
});

/**
 * `/set-password?token=…` — outside the shell like `/sign-in`, for the same
 * reason: whoever opens an invite or forgot-password link has no session to
 * show a sidebar for (M10-6, M10-7).
 */
const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/set-password",
  component: SetPasswordScreen,
});

/**
 * `/docs/attachments/:id` — outside the shell like `/sign-in` and
 * `/set-password`, since it renders nothing of its own to put a sidebar
 * around (`AttachmentRedirect`'s own comment on why this route exists at
 * all). Left unguarded by `beforeLoad`: the download it bounces to is
 * permission-checked server-side on every request regardless (FR-ACL-7), so
 * a client-side session check here would only duplicate that, not replace it.
 */
const attachmentRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/attachments/$id",
  component: AttachmentRedirectRoute,
});

function sectionRoutes(): AnyRoute[] {
  return SHIPPED_MODULES.flatMap((module) =>
    module.sections.map((section) => {
      // "/" + "" is "/"; "/orders" + "" is "/orders"; "/orders" +
      // "/unfulfilled" is "/orders/unfulfilled".
      const path =
        module.path === "/"
          ? `/${section.path.replace(/^\//, "")}`
          : `${module.path}${section.path}`;

      return createRoute({
        getParentRoute: () => shellRoute,
        path: path === "" ? "/" : path,
        component: SectionPage,
        validateSearch: validateSectionSearch,
        /**
         * Which module and section this URL is. The shell reads it back from
         * the matched route rather than parsing the pathname, so a path that
         * changes shape later does not break the highlight.
         */
        staticData: { moduleId: module.id, sectionId: section.id },
      });
    }),
  );
}

/**
 * `/account/notifications` — the signed-in person's own notification choices
 * (E6, #446). Inside the shell, but not one of its modules: the Settings module
 * is administration with every section behind a permission, and this is
 * something every person owns. The API scopes it to the session's own user, so
 * there is no permission for the route to ask for.
 */
const notificationSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/account/notifications",
  component: NotificationSettings,
  // Tells the sidebar this is no module's page, so it highlights nothing
  // rather than the dashboard it falls back to.
  staticData: { accountPage: true },
});

/**
 * `/inventory/low-stock` — the page that became a filter (#602 part B). Old
 * links, bookmarks and a saved habit land on Stock Levels with the Low stock
 * filter on, which is the same rows the page listed.
 */
const lowStockRedirectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/inventory/low-stock",
  component: PageSkeleton,
  beforeLoad: () => {
    redirect({
      to: "/inventory",
      search: {
        filter: encodeFilter({
          kind: "group",
          combinator: "and",
          children: [
            { kind: "condition", field: "belowThreshold", operator: "is_true" },
          ],
        }),
      },
      throw: true,
    });
  },
});

/**
 * `/inventory/adjustments` — the screen that went (#602 part D). Update stock
 * does the same writes, so the old address lands there.
 */
const adjustmentsRedirectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/inventory/adjustments",
  component: PageSkeleton,
  beforeLoad: () => {
    redirect({ to: "/inventory/update-stock", throw: true });
  },
});

const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    ...sectionRoutes(),
    lowStockRedirectRoute,
    adjustmentsRedirectRoute,
    notificationSettingsRoute,
  ]),
  signInRoute,
  setPasswordRoute,
  attachmentRedirectRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: RouteError,
  defaultPendingComponent: PageSkeleton,
  /*
   * Show the skeleton only if the wait is long enough to notice. Below this,
   * a skeleton that flashes and vanishes is worse than a brief nothing.
   */
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
});

export type { SectionSearch };

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  /**
   * What every section route carries. Read by the shell to set the highlight.
   *
   * Optional because the root route has none — making them required would
   * force a meaningless moduleId onto the layout route.
   */
  interface StaticDataRouteOption {
    moduleId?: string;
    sectionId?: string;
    /** A page in the shell that belongs to no module — account settings. */
    accountPage?: boolean;
  }
}
